import { useQuery } from "@tanstack/react-query"
import { useEffect, useState } from "react"

import type { GuildDirectoryMember } from "@/lib/guild-war-wire-types"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { guildWarApi } from "@/lib/guild-war-api"
import { guildWarKeys } from "@/lib/query-keys"

export type ActivityMember = Pick<
  GuildDirectoryMember,
  "memberId" | "displayName"
>

export const activityMemberLabel = (member: ActivityMember) =>
  member.displayName ?? member.memberId

export const selectFirstActivityMember = (
  members: readonly ActivityMember[],
  onSelect: (member: ActivityMember) => void,
) => {
  const member = members[0]
  if (member) onSelect(member)
}

export function ActivityMemberPicker({
  id,
  label = "Member",
  onSelect,
  selectLabel = "Select",
}: {
  id: string
  label?: string
  selectLabel?: string
  onSelect: (member: ActivityMember) => void
}) {
  const [query, setQuery] = useState("")
  const [debouncedQuery, setDebouncedQuery] = useState("")
  const normalizedQuery = query.trim()
  const isDebouncing = normalizedQuery !== debouncedQuery

  useEffect(() => {
    const timeout = globalThis.setTimeout(() => {
      setDebouncedQuery(normalizedQuery)
    }, 200)

    return () => globalThis.clearTimeout(timeout)
  }, [normalizedQuery])

  const membersQuery = useQuery({
    queryKey: guildWarKeys.memberSearch(debouncedQuery),
    queryFn: () =>
      guildWarApi.searchGuildMembers({ query: debouncedQuery, limit: 8 }),
    enabled: debouncedQuery.length >= 2,
  })
  const members = isDebouncing ? [] : (membersQuery.data ?? [])
  const selectFirst = () => selectFirstActivityMember(members, onSelect)

  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex flex-wrap gap-2">
        <Input
          id={id}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault()
              selectFirst()
            }
          }}
          className="w-72"
          placeholder="Search guild display name"
        />
        <Button
          type="button"
          disabled={
            isDebouncing || membersQuery.isLoading || members.length === 0
          }
          onClick={selectFirst}
        >
          {selectLabel}
        </Button>
      </div>
      {normalizedQuery.length >= 2 && (
        <div className="grid gap-1">
          {isDebouncing || membersQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Searching members…</p>
          ) : membersQuery.isError ? (
            <p className="text-sm text-destructive">
              Failed to search members.
            </p>
          ) : members.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No matching members.
            </p>
          ) : (
            members.map((member) => (
              <Button
                key={member.memberId}
                type="button"
                variant="outline"
                className="justify-start"
                onClick={() => onSelect(member)}
              >
                {activityMemberLabel(member)}
              </Button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
